import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { DataSourceRecordBase } from "abweb/dto/generated/ds/DataSourceRecordBase";
import { DataSourceState } from "abweb/dto/generated/ds/DataSourceState";
import { DataSourceSaveAction } from "abweb/dto/generated/ds/DataSourceSaveAction";
import { DebugUtils } from "abweb/utils/DebugUtils";
import { DataSourceValueFactory } from "abweb/dto/ds/DataSourceValueFactory";
import { StringUtils } from "abweb/utils/StringUtils";
import * as AbWebResources from "abweb/generated/locale/AbWebResources";
import { ClassUtils } from "abweb/utils/ClassUtils";
export class DataSourceRecord extends DataSourceRecordBase {
  static createWithDefaults(id, values) {
    return DataSourceRecord.createDataSourceRecord(
    // So why can't this just be called 'create' ?
    null, null, null, id, null, null, DataSourceState.NORMAL, values);
  }
  static createDataSourceRecord(attributeValues, clientTag, dataSourceSaveAction, id, childRecords, updateStamp, state, values, editableOverwrite = null, deletable = false) {
    const retVal = new DataSourceRecord();
    retVal.initDataSourceRecord(attributeValues, clientTag, dataSourceSaveAction, editableOverwrite, id, state, childRecords, deletable, updateStamp, values);
    return retVal;
  }
  constructor() {
    super();
    _defineProperty(this, "_parentRecord", null);
  }
  get refID() {
    // Overridden in DataSourceBaseValue
    return null;
  }
  set refID(value) {
    // Overridden in DataSourceBaseValue
    throw new Error("DataSourceValue does not support refId, much less setting it.");
  }

  /**
   * Gets a field value.
   * Note that this method will honor a dot notation to drill into subrecords however, it will NOT work if the fieldName
   * path has a Vector in the path somewhere. If this is the case, the code simply returns the Vector so be careful
   * using this API if you are dealing with Vectors.
   * @param fieldName Field name in this record, or a dot notation subfield if this record has nested subrecords
   * @returns {any} DSV or null or undefined if not found
   */
  getFieldValue(fieldName) {
    if (StringUtils.isNullOrEmpty(fieldName) || !this.values) {
      return null;
    }
    const dotIndex = fieldName.indexOf(".");
    if (dotIndex < 0) {
      return this.values[fieldName];
    }

    // Assume that the fieldName references a subrecord via dot notation
    // so recurse to find leaf value
    const thisFieldName = fieldName.substring(0, dotIndex);
    const fieldValue = this.values[thisFieldName];
    if (fieldValue == null) {
      return null;
    }
    if (fieldValue.isVector) {
      // Stop here
      // NB: This is a stopgap measure since I am not sure how to proceed if we have vectors
      return fieldValue;
    }
    if (fieldValue.isRecord) {
      const subRecord = fieldValue;
      return subRecord.getFieldValue(fieldName.substring(dotIndex + 1));
    }
    return null;
  }
  findField(fieldPath) {
    if (this._field == null) {
      // Assume this is a top-level record
      if (this._dataSource == null || this._dataSource.recordFormat == null) {
        return null;
      }
      return this._dataSource.recordFormat.findField(fieldPath);
    }
    const recordFormat = this._field.recordFormat;
    if (recordFormat == null) {
      return null;
    }
    return recordFormat.findField(fieldPath);
  }

  /**
   * Sets a value for a fieldPath.
   * @param fieldPath Target field path.
   * @param value Value to set. Could be a simple type or a DataSourceValue.
   * @returns DataSourceValue or may throw if fieldPath does not exist, or
   * if value type cannot be coerced
   * into target type.
   */
  setDataSourceValue(fieldPath, value) {
    let dsv = this.getFieldValue(fieldPath);
    if (dsv == null) {
      // No current value so we have to create one
      const targetField = this.findField(fieldPath);
      if (targetField == null) {
        // No target field so we are going to punt
        throw new Error(`Cannot create DataSourceValue because fieldPath '${fieldPath}' does not exist.`);
      }
      const dotIndex = fieldPath.indexOf(".");
      if (dotIndex > 0) {
        // We have a dotted path, so we need to get the right subrecord
        const fieldName = fieldPath.substr(0, dotIndex);
        let subRecord = this.getFieldValue(fieldName);
        if (subRecord == null) {
          // Record not there so need to create it
          subRecord = this.createSubRecord(fieldName);
        } else if (!subRecord.isRecord) {
          throw new Error(`Invalid fieldPath '${fieldPath}': '${fieldName}' is not a DataSourceRecord`);
        }
        return subRecord.setDataSourceValue(fieldPath.substr(dotIndex + 1), value);
      }

      // fieldPath is a fieldName so use targetField to create right type
      dsv = DataSourceValueFactory.createFromField(targetField);
      dsv._containerRecord = this;
      if (!dsv.coerceAndSetCurrentValue(value)) {
        // We didn't change value so make sure validationError is properly set
        dsv.updateValidationError();
      }
      if (this.values == null) {
        throw new Error(`Cannot create DataSourceValue for ${fieldPath}: values does not exist.`);
      }

      // This is a new field so we have to set it in the values map
      this.values[fieldPath] = dsv;
    } else {
      // We have an existing value so just update it
      dsv.coerceAndSetCurrentValue(value);
    }
    return dsv;
  }
  createSubRecord(fieldName) {
    const field = this.findField(fieldName);
    if (field == null) {
      throw new Error(`Cannot create subrecord. Field '${fieldName}' is not defined.`);
    }
    const subRecord = DataSourceRecord.createWithDefaults(null, {});
    subRecord._field = field;
    subRecord._containerRecord = this;
    if (this.values == null) {
      throw new Error(`Cannot create subcord for ${fieldName}: values does not exist.`);
    }

    // This is a new field so we have to set it in the values map
    this.values[fieldName] = subRecord;
    return subRecord;
  }

  /**
   * Flag indicating if this record has 1 or more childRecords.
   * @returns {boolean}
   */
  get hasChildRecords() {
    return this.childRecords != null && this.childRecords.length > 0;
  }
  get depth() {
    let depth = 0;
    let rec = this;
    while (rec._parentRecord != null) {
      depth++;
      rec = rec._parentRecord;
    }
    return depth;
  }

  /**
   * Gets top-level record associated with this record.
   * This will typically return itself unless the record is a nested subrecord.
   */
  getTopLevelRecord() {
    let rec = this;
    while (rec != null) {
      const next = rec._containerRecord;
      if (next == null) {
        return rec;
      }
      rec = next;
    }
    return null;
  }

  /**
   * Determines if the argument is a descendant of this record.
   * @param rec Record to test.
   * @returns true if rec is a child record or a descendant, false otherwise.
   */
  isDescendantRecord(rec) {
    while (rec != null) {
      if (rec._parentRecord === this) {
        return true;
      }
      rec = rec._parentRecord;
    }
    return false;
  }

  /**
   * Gets the current value part of this DataSourceValue.
   * Initially, the currentValue is the same as value, but if the client
   * makes local changes, the currentValue represents those local changes.
   * This getter MUST be overridden by subclasses to return the value field.
   */
  getCurrentValue() {
    throw new Error("DataSourceValue.getCurrentValue() should not be invoked on DataSourceRecord");
  }

  /**
   * Gets the initial value part of this DataSourceValue.
   * This getter MUST be overridden by subclasses to return the value field.
   */
  getValue() {
    throw new Error("DataSourceValue.getValue() should not be invoked on DataSourceRecord");
  }

  /**
   * Gets the current baseline part of this DataSourceValue.
   * This getter MUST be overridden by subclasses to return the value field.
   */
  getBaseline() {
    throw new Error("DataSourceValue.getBaseline() should not be invoked on DataSourceRecord.");
  }
  getDisplayValue() {
    // Poor man's abstract base method

    // note that we had to come up with *some* display string due to a bug in, of all things, the SelectionModel code
    // used in the AbRepeatCmp component. It was incorrectly establishing a selected item in an otherwise unselectable
    // list of things, and then trying to display information about the invalid selection. Ultimately it called toString()
    // on the record, which flows through this method. The error being thrown broke the repeat components ability to
    // render all of its items. By implementing this method, the SelectionModel/AbRepeatCmp is still doing the WRONG THING
    // under the covers, but to the end user, everything should appear to be working.
    return `DataSourceRecord (id='${this.id}')`;
    //throw new Error("getDisplayValue: should not be invoked on DataSourceRecord.");
  }
  get isRecord() {
    return true;
  }
  onLocalStateChanged() {
    super.onLocalStateChanged();
    if (this._containerRecord == null && this._dataSource != null) {
      // bubble this change up to DataSource
      this._dataSource.onRecordHasLocalChangeChanged(this);
    }

    // DG-511: inform values that localState has changed because their isAddedLocal, isDeletedLocal depend on my value
    const fieldNames = Object.keys(this.values);
    for (const fieldName of fieldNames) {
      const dsv = this.getFieldValue(fieldName);
      if (dsv != null) {
        dsv.onContainerRecordLocalStateChanged();
      }
    }
  }
  updateLocalStateValue() {
    if (this.isAddedLocal || this.isDeletedLocal) {
      return;
    }
    if (this.isReparented) {
      this.set("localState", DataSourceState.CHANGE);
      return;
    }
    let newValue = DataSourceState.NORMAL;
    const fieldNames = Object.keys(this.values);
    for (const fieldName of fieldNames) {
      const dsv = this.getFieldValue(fieldName);
      if (dsv != null && dsv.hasLocalChange) {
        newValue = DataSourceState.CHANGE;
        break;
      }
    }
    this.set("localState", newValue);
  }

  /**
   * Notification that a contained field value's validationError value changed.
   * @param dsv
   */
  onContainerValueValidationErrorChanged(dsv) {
    if (!dsv.isValid()) {
      // If any contained field value is not valid, then neither am I
      this.set("validationError", AbWebResources.dataSourceRecord_invalid());
    } else {
      // dsv transitioned from being invalid to valid, so we have to check all values
      this.updateValidationError();
    }
  }

  /**
   * Called whenever a contained field value's validationError changes, this method updates the validationError based on
   * contained values.
   * You can also call this method to force the validationError to be updated.
   * This may be needed since the server will not set this value.
   */
  updateValidationError() {
    let isValid = true;
    for (const fieldKey of Object.keys(this.values)) {
      const dsv = this.getFieldValue(fieldKey);
      if (dsv != null) {
        if (!dsv.isValid()) {
          isValid = false;
          break;
        }
      }
    }
    if (isValid) {
      if (!this.isValid()) {
        // reset it
        this.set("validationError", "");
      }
    } else {
      if (this.isValid()) {
        this.set("validationError", AbWebResources.dataSourceRecord_invalid());
      }
    }
  }
  get isReparented() {
    return this._originalParentRecord !== undefined && this.parentRecord !== this._originalParentRecord;
  }
  revertLocalChange() {
    if (!this.hasLocalChange) {
      return false;
    }
    if (this.isDeletedLocal) {
      // Undelete this record
      return this.markForDelete(true);
    }
    if (this.isAddedLocal) {
      DebugUtils.developerError("revert on a locally ADDed record should not be done directly on the record");
      return false;
    }
    if (this.isReAddedLocal) {
      // Record was reverted from a DELETE state which goes into RE_ADD
      // so if we revert again, we should return to the DELETE state
      // but note that while we were in the RE_ADD state, we may have changed some items so we don't return here
      this.markForDelete(false);
    }
    if (this.isReparented) {
      // This record has been reparented
      // so move it back
      if (this._originalParentRecord == null) {
        // Move back to top level
        // Note that we don't have any way of preserving order
        if (this._dataSource != null) {
          this._dataSource.insertRecords(-1, [this]);
        }
      } else {
        // Move back to its original parent
        this._originalParentRecord.insertChildRecords(-1, [this]);
      }
    }
    const fieldNames = Object.keys(this.values);
    for (const fieldName of fieldNames) {
      const dsv = this.getFieldValue(fieldName);
      if (dsv != null) {
        dsv.revertLocalChange();
      }
    }

    // TODO: Can records have fields with localState = ADD/DELETE?

    this.set("localState", DataSourceState.NORMAL);
    return true;
  }

  /**
   * Used primarily by DataSource.revertLocalChanges().
   * This method guarantees that the revert will recurse into all descendant childRecords.
   * You should not normally have to call this method to revert the changes in a record.
   * Just use the revertLocalChanges() method instead.
   * @returns {boolean}
   */
  revertLocalChangeAndDescend() {
    const needToDescend = !this.isDeletedLocal;
    const ret = this.revertLocalChange();
    if (needToDescend && this.hasChildRecords) {
      for (let n = this.childRecords.length - 1; n >= 0; n--) {
        const childRec = this.childRecords[n];
        if (childRec) {
          if (childRec.isAddedLocal) {
            // Special case where we need to explicitly remove the record here
            this.removeChildRecord(n);
          } else {
            childRec.revertLocalChangeAndDescend();
          }
        }
      }
    }
    return ret;
  }
  markForDelete(undelete = false) {
    if (!undelete && !this.isDeletedLocal && !this.isAddedLocal && this.hasLocalChange && !this.isReAddedLocal) {
      // Revert local changes on fields first
      this.revertLocalChange();
    }
    if (undelete && !this.canUndelete()) {
      // I can't be undeleted because my ancestor is marked for delete
      return false;
    }
    const ret = super.markForDelete(undelete);
    if (!ret) {
      // No change in state
      return ret;
    }
    if (this.hasChildRecords) {
      // Recurse into descendants
      for (let n = this.childRecords.length - 1; n >= 0; n--) {
        const childRec = this.childRecords[n];
        if (undelete) {
          childRec.markForDelete(undelete);
        } else {
          // Call this so that proper notifications are fired
          this.removeChildRecord(n);
        }
      }
    }
    return ret;
  }

  /**
   * Returns whether this record can be undeleted.
   * This is relevant for hierarchical data where you cannot undelete a record if it is marked as
   * deleted as a result of an ancestor record being marked for deletion.
   */
  canUndelete() {
    if (!(this.isDeletedLocal || this.isDeleted)) {
      // Since I am not marked as deleted, I can't be undeleted
      return false;
    }
    if (this._parentRecord == null) {
      return true;
    }
    return !(this._parentRecord.isDeletedLocal || this._parentRecord.isDeleted);
  }
  get editable() {
    if (this.editableOverwrite != null) {
      return this.editableOverwrite;
    }
    if (this._containerRecord != null && !this._containerRecord.editable) {
      // Container record is not editable, so we can't be either
      return false;
    }

    // if record has a field, convert its undefined | null to false.  if record doesn't have a field, then it is editable
    return this._field ? !!this._field.editable : true;
  }
  findRecordById(id) {
    if (this.id === id) {
      return this;
    }
    if (!this.hasChildRecords) {
      return null;
    }
    for (const childRec of this.childRecords) {
      const rec = childRec.findRecordById(id);
      if (rec != null) {
        return rec;
      }
    }
    return null;
  }
  findRecordByClientTag(tag) {
    if (this.clientTag === tag) {
      return this;
    }
    if (!this.hasChildRecords) {
      return null;
    }
    for (const childRec of this.childRecords) {
      const rec = childRec.findRecordByClientTag(tag);
      if (rec != null) {
        return rec;
      }
    }
    return null;
  }
  prepareValueForSave() {
    if (this.isReparented) {
      this.dataSourceSaveAction = DataSourceSaveAction.ADD;
    } else {
      super.prepareValueForSave();
    }
    if (this.id == null && this.clientTag == null) {
      // Probably a new record so we need a cookie for reconciliation
      this.clientTag = this.generateRecordCookie();
    }
    if (this.isAddedLocal || this.isDeletedLocal) {
      // If the record is marked for local add or delete, we can skip the walk of its contained items
      return;
    }

    // Process field values now
    for (const fieldName of Object.keys(this.values)) {
      const dsv = this.getFieldValue(fieldName);
      if (dsv != null) {
        dsv.prepareValueForSave();
      }
    }
  }

  /**
   * Creates a not-so-deep clone of this record for saving to server.
   * The reason for the clone is so that we don't send a full hierarchy of records. Thus, the clone
   * initially has an empty childRecords Array. Other than that, it is a shallow clone of changed fields.
   */
  createSaveClone() {
    // Note the
    const saveClone = DataSourceRecord.createDataSourceRecord(this.attributeValues, this.clientTag, this.dataSourceSaveAction, this.id, [] /* childRecords */, this.updateStamp, this.state, this.values);
    saveClone._originalParentRecord = this.originalParentRecord;
    saveClone._parentRecord = this.parentRecord;
    saveClone.state = this.state;
    saveClone.localState = this.localState;
    saveClone.hasLocalChange = this.hasLocalChange;
    saveClone.prepareValueForSave();
    return saveClone;
  }
  generateRecordCookie() {
    const cookie = DataSourceRecord.nextCookie++;
    return cookie.toString();
  }
  coerceValue(value) {
    // No implementation for record
    return null;
  }

  /**
   * Insert new child records.
   * Make sure you use this API to insert child records. Do not manipulate the childRecords Array directly.
   * Otherwise the proper notifications will not fire.
   * Also note that this API can be used to reparent items from their original parent under this as a parent.
   * Note that the parentRecord for all items must be the same.
   * You can also use this API to move records inside the same parent.
   * @param startIndex Zero-based start index in the childRecords Array of where the insertion should be done.
   * Pass -1 to append.
   * If you use this as a move inside the same parent, it is up to you to properly calculate the correct startIndex
   * since step one is to remove items which will change the current order and content of childRecords. So you should
   * calculate your startIndex assuming that items have been removed from childRecords first.
   * @param items Array of DataSourceRecords to insert.
   * Note that that if reparenting, all items parentRecord must be the same.
   */
  insertChildRecords(startIndex, items) {
    if (items == null || items.length === 0) {
      return;
    }
    if (this.childRecords == null) {
      this.childRecords = [];
    }
    const isLocalMove = items[0].parentRecord === this;
    this.removeChildItemsBeforeMove(items);
    if (startIndex < 0) {
      startIndex = this.childRecords.length;
    }

    // Make sure the item fields are properly set
    for (const item of items) {
      item._dataSource = this._dataSource;
      if (!item.isAddedLocal && item._originalParentRecord === undefined) {
        // Save this for revert
        item._originalParentRecord = item.parentRecord;
      }
      item._parentRecord = this;
      if (!item.isAddedLocal) {
        if (!isLocalMove && this === item._originalParentRecord) {
          // We reparented back to the original one,
          // so we need to be more careful about setting localState
          item.updateLocalStateValue(); // Need to check contained values
        } else {
          item.set("localState", DataSourceState.CHANGE);
        }
      }
    }
    Array.prototype.splice.apply(this.childRecords, [startIndex, 0, ...items]);
    if (this._dataSource != null) {
      this._dataSource.onRecordChildRecordsAdded(this, startIndex, items);
    }
  }

  /**
   * Called from insertChildRecords() this helper function to remove an Array of records from their parent
   * before inserting them here.
   * @param items Items to remove from their parent.
   * Note they must all have a common parent, or we throw an Error.
   */
  removeChildItemsBeforeMove(items) {
    const oldParentRecord = items[0].parentRecord;
    const indices = [];
    let prevIndex = -1;
    let areSequential = true;
    for (const item of items) {
      if (item.parentRecord !== oldParentRecord) {
        throw new Error("Cannot insert child records because source items do not all have the same parentRecord");
      }
      let index;
      if (oldParentRecord == null) {
        // These are top-level records (maybe)
        if (this._dataSource == null) {
          // Nothing I can do here
          return;
        } else {
          index = this._dataSource.records.indexOf(item);
          if (index < 0) {
            // Assume these are new records so skip this processing
            return;
          }
        }
      } else {
        if (!oldParentRecord.childRecords) {
          throw new Error("Cannot insert child: no childRecords to insert into");
        }
        index = oldParentRecord.childRecords.indexOf(item);
        if (index < 0) {
          throw new Error("Cannot insert child records because items not in childRecords of their parent");
        }
      }
      indices.push(index);
      if (indices.length > 1) {
        if (index !== prevIndex + 1) {
          areSequential = false;
        }
      }
      prevIndex = index;
    }
    if (oldParentRecord == null) {
      if (this._dataSource == null) {
        return;
      }

      // These are top-level records
      if (areSequential) {
        const index = indices[0];
        this._dataSource.records.splice(index, items.length);
      } else {
        // Have to do them one by one
        // and worse, what if they aren't in order?
        // Keep finding max index and remove it
        const items1 = items.slice();
        while (indices.length > 0) {
          const index = getMaxIndex();
          const itemIndex = indices[index];
          this._dataSource.records.splice(itemIndex, 1);
          indices.splice(index, 1);
          items1.splice(index, 1);
        }
      }
    } else {
      // Remove items from their current parentRecord
      if (areSequential) {
        // We can do this in 1 call
        const index = indices[0];
        if (oldParentRecord.childRecords) {
          oldParentRecord.childRecords.splice(index, items.length);
        }
        if (this._dataSource != null) {
          this._dataSource.onRecordChildRecordsRemoved(oldParentRecord, index, items);
        }
      } else {
        // Have to do them one by one
        // and worse, what if they aren't in order?
        // Keep finding max index and remove it
        const items1 = items.slice();
        while (indices.length > 0) {
          const index = getMaxIndex();
          const itemIndex = indices[index];
          const item = items1[index];
          if (oldParentRecord.childRecords) {
            oldParentRecord.childRecords.splice(itemIndex, 1);
          }
          if (this._dataSource != null) {
            this._dataSource.onRecordChildRecordsRemoved(oldParentRecord, index, [item]);
          }
          indices.splice(index, 1);
          items1.splice(index, 1);
        }
      }
    }
    function getMaxIndex() {
      let imax = -1;
      let max = -1;
      for (let n = 0; n < indices.length; n++) {
        if (indices[n] > max) {
          imax = n;
          max = indices[n];
        }
      }
      return imax;
    }
  }

  /**
   * Remove a child record.
   * Note that the record will only be removed from the childRecords Array if its localState = ADD. Otherwise,
   * it will be marked for delete.
   * Make sure you use this API to remove child records. Do not manipulate the childRecords Array directly.
   * Otherwise the proper notifications will not fire.
   * @param arg Zero-based index of record to remove or the record instance to remove.
   * @returns Record removed or marked for removal.
   */
  removeChildRecord(arg) {
    if (!this.hasChildRecords) {
      return null;
    }
    let index = -1;
    if (typeof arg === "number") {
      index = arg;
      if (index < 0 || index >= this.childRecords.length) {
        // Out of bounds
        return null;
      }
    } else if (ClassUtils.isInstanceOf(arg, DataSourceRecord)) {
      index = this.childRecords.indexOf(arg);
    }
    if (index < 0) {
      return null;
    }
    const rec = this.childRecords[index];
    if (rec == null) {
      return null; // nothing to remove or to mark for delete
    }
    if (rec.isAddedLocal) {
      // We can remove this item
      this.childRecords.splice(index, 1);
      if (this._dataSource != null) {
        this._dataSource.onRecordChildRecordsRemoved(this, index, [rec]);
      }
    } else {
      // Mark it as removed
      const oldValue = rec.hasLocalChange;
      rec.markForDelete();
      if (oldValue !== rec.hasLocalChange) {
        // Child record notifications will not bubble up
        if (this._dataSource != null) {
          this._dataSource.onRecordHasLocalChangeChanged(rec);
        }
      }
    }
    return rec;
  }
  get originalParentRecord() {
    return this._originalParentRecord;
  }
  get parentRecord() {
    return this._parentRecord;
  }

  /**
   * Returns flattened array of descendant records.
   * @returns {DataSourceRecord[]} Note that the return array includes the record instance itself.
   */
  getDescendantRecords() {
    let output = [];
    getDescendantRecordsHelper(this);
    return output;
    function getDescendantRecordsHelper(rec) {
      output.push(rec);
      if (rec.childRecords == null) {
        return;
      }
      for (let childRec of rec.childRecords) {
        getDescendantRecordsHelper(childRec);
      }
    }
  }

  /**
   * Gets a field value from a subrecord.
   * @param {DataSourceRecord} subRec Subrecord value.
   * @param {string} fieldPath Field path
   * @returns {DataSourceValue | null}
   */
  static getSubRecordField(subRec, fieldPath) {
    const superPath = subRec._field.parentField.qualifiedName;
    const index = fieldPath.indexOf(superPath);
    if (index < 0) {
      return null;
    }
    const subPath = fieldPath.substr(superPath.length + 1); // Move past dot
    return subRec.getFieldValue(subPath);
  }
}
_defineProperty(DataSourceRecord, "nextCookie", 0);
//# sourceMappingURL=DataSourceRecord.js.map
